import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, Observable, startWith } from 'rxjs';

import {
  CanvasActionsService,
  DevicesService,
  FeatureFlagsConfigService,
  NetworkSettingsComponent,
  PaletteManagerService,
  PerformanceComponent,
  PreferenceManagerComponent,
  ReadingsManagerComponent,
  ReadingsManagerDialogNotification,
  ReadingsService,
} from '@olympus/ngx-shared';
import { DialogService } from 'shared-modules-angular/dialog';

import { ConfigService } from '../../services/config';

@Component({
  selector: 'app-settings-menu',
  templateUrl: './settings-menu.component.html',
  standalone: false,
})
export class SettingsMenuComponent {
  public hasPerformanceSettings: boolean;
  public hasNetworkSettings: boolean;
  public featureFlagInspection: boolean;
  public featureFlagDeviceManager: boolean;
  public featureFlagStatistics = false;
  public featureFlagAnalysis: boolean;
  public inspectionInProgress$: Observable<boolean> = this._canvasActionsService.isInspectionMode$.pipe(
    combineLatestWith(this._canvasActionsService.isPauseMode$),
    map(([isInspection, isPause]) => isInspection || isPause),
    startWith(false),
  );

  constructor(
    private readonly _dialogService: DialogService,
    private readonly _paletteManagerService: PaletteManagerService,
    private readonly _featureFlagsConfigService: FeatureFlagsConfigService,
    private readonly _readingsService: ReadingsService,
    private readonly _canvasActionsService: CanvasActionsService,
    private readonly _deviceManagerService: DevicesService,
    private readonly _config: ConfigService,
  ) {
    this.hasPerformanceSettings = this._config.hasPerformanceSettings;
    this.hasNetworkSettings = this._config.hasNetworkSettings;
    this.featureFlagDeviceManager = this._featureFlagsConfigService.appConfig.features.deviceManager;
    this.featureFlagStatistics = this._featureFlagsConfigService.appConfig.features.zoneStatistics;

    this._featureFlagsConfigService.availableFeatures$.pipe(takeUntilDestroyed()).subscribe((features) => {
      this.featureFlagInspection = features.includes('inspection');
      this.featureFlagAnalysis = features.includes('analysis');
    });
  }

  public openReadingManagers(): void {
    const notification: ReadingsManagerDialogNotification = {
      headerTitle: 'Readings Manager',
      readingsPreference: this._readingsService.getReadingsPreference(ReadingsService.READING_PREFERENCE_KEY),
      availableFilters: this._readingsService.getFilters(),
      availableReadings: this._readingsService.getAllReadings(),
      resultReadingsPreference: null,
    };

    this._dialogService
      .open<ReadingsManagerDialogNotification, ReadingsManagerDialogNotification, ReadingsManagerComponent>(
        ReadingsManagerComponent,
        {
          data: notification,
          height: ReadingsManagerComponent.HEIGHT,
          width: ReadingsManagerComponent.WIDTH,
          disableClose: true,
        },
      )
      .closed
      .subscribe((result) => {
        this._readingsService.setReadingsPreference(ReadingsService.READING_PREFERENCE_KEY, result?.resultReadingsPreference);
      });
  }

  public openStatisticsManagers(): void {
    if (!this.featureFlagStatistics) {
      return;
    }

    const notification: ReadingsManagerDialogNotification = {
      headerTitle: 'Readings Statistic Manager',
      readingsPreference: this._readingsService.getReadingsPreference(ReadingsService.STATISTICS_PREFERENCE_KEY),
      availableFilters: this._readingsService.getStatisticFilters(),
      availableReadings: this._readingsService.getAllReadingsStatistic(),
      resultReadingsPreference: null,
    };

    this._dialogService
      .open<ReadingsManagerDialogNotification, ReadingsManagerDialogNotification, ReadingsManagerComponent>(
        ReadingsManagerComponent,
        {
          data: notification,
          height: ReadingsManagerComponent.HEIGHT,
          width: ReadingsManagerComponent.WIDTH,
          disableClose: true,
        },
      )
      .closed
      .subscribe((result) => {
        this._readingsService.setReadingsPreference(ReadingsService.STATISTICS_PREFERENCE_KEY, result?.resultReadingsPreference);
      });
  }

  public openPaletteManager(): void {
    this._paletteManagerService.open();
  }

  public openPreferenceManager(): void {
    this._dialogService.open(
      PreferenceManagerComponent,
      {
        height: PreferenceManagerComponent.HEIGHT,
        width: PreferenceManagerComponent.WIDTH,
        disableClose: true,
      },
    );
  }

  public openPerformanceModal(): void {
    this._dialogService.open(
      PerformanceComponent,
      {
        height: PerformanceComponent.HEIGHT,
        width: PerformanceComponent.WIDTH,
        disableClose: true,
      },
    );
  }

  public openNetworkSettings(): void {
    this._dialogService.open(
      NetworkSettingsComponent,
      {
        height: NetworkSettingsComponent.HEIGHT,
        width: NetworkSettingsComponent.WIDTH,
        disableClose: true,
      },
    );
  }

  public openDeviceManager(): void {
    this._deviceManagerService.open();
  }
}
